import React from 'react';
import Button from 'react-bootstrap/Button';
import Collapse from 'react-bootstrap/Collapse';
import { Section } from '../../models/globals';
import { ExpandableHtml, ExpandableHtmlProps } from './ExpandableHtml';
import { ProductSection, ProductSectionProps } from './ProductSection';

interface ExpandableHtmlSectionProps extends ProductSectionProps {
    initialShownCount?: number;
    items: Section[];
    showAllBtnText?: string;
}

const ExpandableHtmlSection: React.FC<ExpandableHtmlSectionProps> = ({
    initialShownCount = 3,
    items,
    showAllBtnText = 'Browse All',
    ...productSectionProps
}) => {
    if (initialShownCount === items.length - 1) {
        initialShownCount = items.length; // hiding 1 item is pointless, just show them all
    }
    const topItems = items.slice(0, initialShownCount);
    const remainingItems = items.slice(initialShownCount);
    const [allExpanded, setAllExpanded] = React.useState(false);
    const [allShown, setAllShown] = React.useState(false);
    const toggleExpandAll = () => {
        setAllExpanded((expanded) => {
            setAllShown(!expanded);
            return !expanded;
        });
    };

    if (!items.length) return null;

    return (
        <ProductSection className="bg-light expandable-section" {...productSectionProps}>
            {items.length > 1 && (
                <Button
                    aria-expanded={allExpanded}
                    className="px-0 text-decoration-none text-primary"
                    onClick={toggleExpandAll}
                    variant="link"
                >
                    {allExpanded ? 'Collapse All' : 'Expand All'}
                </Button>
            )}

            <ExpandableItems allExpanded={allExpanded} items={topItems} />

            {!!remainingItems.length && (
                <>
                    {!allShown && (
                        <Button
                            aria-pressed={allShown}
                            className="px-0 text-decoration-none text-primary"
                            onClick={() => setAllShown(true)}
                            variant="link"
                        >
                            {showAllBtnText}
                        </Button>
                    )}

                    <Collapse in={allShown}>
                        <div>
                            <ExpandableItems allExpanded={allExpanded} items={remainingItems} />
                        </div>
                    </Collapse>
                </>
            )}
        </ProductSection>
    );
};

interface ExpandableItemsProps {
    allExpanded?: boolean;
    items: ExpandableHtmlProps[];
}

const ExpandableItems: React.FC<ExpandableItemsProps> = ({ allExpanded, items }) => {
    return (
        <>
            {items.map(({ content, header }) => (
                <ExpandableHtml content={content} controlledExpanded={allExpanded} header={header} key={header} />
            ))}
        </>
    );
};

export { ExpandableHtmlSection, ExpandableHtmlSectionProps };
